import type { ContentLanguage, Locale } from '@/locales/supportedLocales.ts';

export type { ContentLanguage, Locale } from '@/locales/supportedLocales.ts';
export { contentLanguageForLocale, localeForContentLanguage } from '@/locales/supportedLocales.ts';

export type LanguageTranslationMap<T = string> = Partial<Record<ContentLanguage, T | null>>;
export type RegionalTranslationMap<T = string> = Partial<Record<Locale, T | null>>;

export type TranslationDomain = 'content-language' | 'regional';

export interface ContentDisplayName {
    display_name?: LanguageTranslationMap | null;
    name?: string | null;
}

export interface RegionalDisplayName {
    display_name?: RegionalTranslationMap | null;
    name?: string | null;
}

export interface TranslatableEmailSignature {
    signature_translations?: LanguageTranslationMap | null;
}

export interface TranslatableVdpUrl {
    vdp_url?: LanguageTranslationMap | null;
}

export interface TranslatableAccountLogo {
    logo_translations?: LanguageTranslationMap | null;
}

export type LocaleRegistryEntry = string | { code: string };

const localeCode = (locale: LocaleRegistryEntry): string =>
    typeof locale === 'string' ? locale : locale.code;

const CONTENT_LANGUAGE_PATTERN = /^[a-z]{2,3}$/;
const LOCALE_PATTERN = /^[a-z]{2,3}-[A-Z]{2}$/;

const normalizeRegisteredCode = (
    value: unknown,
    registry: readonly LocaleRegistryEntry[]
): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim().toLocaleLowerCase();

    if (!normalizedValue) {
        return null;
    }

    return (
        registry
            .map(localeCode)
            .find((registeredCode) => registeredCode.toLocaleLowerCase() === normalizedValue) ??
        null
    );
};

export const normalizeContentLanguage = (
    value: unknown,
    registry: readonly LocaleRegistryEntry[]
): ContentLanguage | null => {
    const registeredCode = normalizeRegisteredCode(value, registry);

    return registeredCode !== null && CONTENT_LANGUAGE_PATTERN.test(registeredCode)
        ? (registeredCode as ContentLanguage)
        : null;
};

export const normalizeLocale = (
    value: unknown,
    registry: readonly LocaleRegistryEntry[]
): Locale | null => {
    const registeredCode = normalizeRegisteredCode(value, registry);

    return registeredCode !== null && LOCALE_PATTERN.test(registeredCode)
        ? (registeredCode as Locale)
        : null;
};

const isTranslationMap = <T>(translations: unknown): translations is Record<string, T | null> =>
    typeof translations === 'object' && translations !== null && !Array.isArray(translations);

const readExactTranslation = <T>(translations: Record<string, T | null>, key: string): T | null => {
    const translationKey = Object.hasOwn(translations, key)
        ? key
        : Object.keys(translations).find(
              (candidate) => candidate.toLocaleLowerCase() === key.toLocaleLowerCase()
          ) ?? null;

    if (translationKey === null) {
        return null;
    }

    return translations[translationKey] ?? null;
};

const readTranslation = <T>(
    translations: Record<string, T | null> | null | undefined,
    key: string,
    fallbackKey: string | null
): T | null => {
    if (!isTranslationMap<T>(translations)) {
        return null;
    }

    const value = readExactTranslation(translations, key);

    if (value !== null) {
        return value;
    }

    if (fallbackKey === null || fallbackKey === key) {
        return null;
    }

    return readExactTranslation(translations, fallbackKey);
};

/**
 * Resolve tenant-authored content by base language. Regional keys are never inspected.
 */
export const getContentTranslation = <T>(
    translations: LanguageTranslationMap<T> | null | undefined,
    language: ContentLanguage,
    fallbackLanguage: ContentLanguage | null = null
): T | null => readTranslation<T>(translations, language, fallbackLanguage);

/**
 * Resolve shared product copy by exact regional locale and its configured regional fallback.
 * Base-language aliases are deliberately not probed after the regional cutover.
 */
export const getRegionalTranslation = <T>(
    translations: RegionalTranslationMap<T> | null | undefined,
    locale: Locale,
    fallbackLocale: Locale | null = null
): T | null => readTranslation<T>(translations, locale, fallbackLocale);

const nonBlankDisplayName = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value : null;

/** Resolve an account-owned display name through the content-language domain only. */
export const getContentDisplayName = (
    entity: ContentDisplayName,
    language: ContentLanguage,
    fallbackLanguage: ContentLanguage | null = null,
    domainFallback: string | null = entity.name ?? null
): string | null =>
    nonBlankDisplayName(getContentTranslation(entity.display_name, language, fallbackLanguage)) ??
    domainFallback;

/** Resolve a shared display name through the exact regional-locale domain only. */
export const getRegionalDisplayName = (
    entity: RegionalDisplayName,
    locale: Locale,
    fallbackLocale: Locale | null = null,
    domainFallback: string | null = entity.name ?? null
): string | null =>
    nonBlankDisplayName(getRegionalTranslation(entity.display_name, locale, fallbackLocale)) ??
    domainFallback;

/**
 * Return a copy of the canonical content-language map.
 */
export const getEmailSignatureTranslations = (
    emailConfig: TranslatableEmailSignature | null | undefined
): LanguageTranslationMap => {
    return isTranslationMap<string>(emailConfig?.signature_translations ?? null)
        ? { ...emailConfig?.signature_translations }
        : {};
};

export const getContentEmailSignature = (
    emailConfig: TranslatableEmailSignature | null | undefined,
    language: ContentLanguage
): string | null => getContentTranslation(getEmailSignatureTranslations(emailConfig), language);

export const getContentVdpUrl = (
    inventoryUnit: TranslatableVdpUrl,
    language: ContentLanguage,
    fallbackLanguage: ContentLanguage | null = null
): string | null => {
    return getContentTranslation(inventoryUnit.vdp_url, language, fallbackLanguage);
};

export const getReciprocalContentLanguage = (language: ContentLanguage): ContentLanguage | null => {
    if (language === 'fr') {
        return 'en';
    }

    if (language === 'en') {
        return 'fr';
    }

    return null;
};

/**
 * Read the already-public Account media URL map. URL conversion remains a backend responsibility;
 * the frontend must never receive or transform the underlying storage key.
 */
export const getContentAccountLogo = (
    account: TranslatableAccountLogo | null | undefined,
    language: ContentLanguage
): string | null => {
    return getContentTranslation(account?.logo_translations, language);
};

const setExactTranslation = <T>(
    translations: Partial<Record<string, T | null>> | null | undefined,
    key: string,
    value: T | null
): Partial<Record<string, T | null>> => {
    const updatedTranslations = translations ? { ...translations } : {};

    Object.keys(updatedTranslations)
        .filter((candidate) => candidate.toLocaleLowerCase() === key.toLocaleLowerCase())
        .forEach((candidate) => delete updatedTranslations[candidate]);

    if (value === null) {
        return updatedTranslations;
    }

    return { ...updatedTranslations, [key]: value };
};

/** Null removes a missing value; explicit empty values remain in the language map. */
export const setContentTranslation = <T>(
    translations: LanguageTranslationMap<T> | null | undefined,
    language: ContentLanguage,
    value: T | null
): LanguageTranslationMap<T> => {
    if (!CONTENT_LANGUAGE_PATTERN.test(language)) {
        throw new RangeError(`Unsupported content language [${String(language)}].`);
    }

    return setExactTranslation(translations, language, value) as LanguageTranslationMap<T>;
};

/** Null removes a missing value; explicit empty values remain under the exact regional key. */
export const setRegionalTranslation = <T>(
    translations: RegionalTranslationMap<T> | null | undefined,
    locale: Locale,
    value: T | null
): RegionalTranslationMap<T> => {
    if (!LOCALE_PATTERN.test(locale)) {
        throw new RangeError(`Unsupported locale [${String(locale)}].`);
    }

    return setExactTranslation(translations, locale, value) as RegionalTranslationMap<T>;
};
